/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';

import { BuilderPermission, JobOrderMethod } from '@gh/builder-shared-data';
import { isJobEditable } from '@gh/builder-shared';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  MutablePageState,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  emptySelectionModel,
  isSelectionModelEmpty,
  newSelectionModel,
  SelectionModel,
  selectionModelSelectAll,
} from '@gh/core-data';
import { FormMode } from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { filter$ } from '@gh/rx/operators';

import { sortBy, uniq } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  createJobOrderListPredicate,
  getJobOrderListItemId,
  isJobOrderSelectable,
  JOB_ORDER_LIST_DEFAULT_PRODUCE_OPTIONS,
  JobOrderList,
  JobOrderDetailsAttachment,
  JobOrderListEstimatingCriteria,
  JobOrderListItem,
  JobOrderListMasterInfo,
  JobOrderListProduceOptions,
  JobOrderListProduceStatus,
  JobOrderListSelectionFilter,
  JobOrderListSelectionType,
} from '../../resources/job-order-list.model';

@Injectable()
export class JobOrderDetailsFormState extends MutablePageState<JobOrderList> {
  costCenterCodes: number[];
  orderNumbers: number[];

  private produceStatus$$ = new BehaviorSubject<Maybe<JobOrderListProduceStatus>>(void 0);

  get jobId(): ID {
    return getFormStateSnapshotValue(this.value.job).id;
  }

  get masterInfo(): FormStateSnapshot<JobOrderListMasterInfo> {
    return this.value.job;
  }

  get orders(): JobOrderListItem[] {
    return this.value.orders;
  }

  get selectedOrders(): JobOrderListItem[] {
    return this.value.orders.filter((order) =>
                                      this.value.orderSelection.idList.includes(getJobOrderListItemId(order)));
  }

  get forcePrint(): boolean {
    return this.options.forcePrint;
  }

  get hasSelectedOrdersToEmail(): boolean {
    return this.selectedOrders.some((order) => order.method === JobOrderMethod.Email);
  }

  get hasSelectedOrdersToPrint(): boolean {
    return this.selectedOrders.some((order) => order.method === JobOrderMethod.Printer);
  }

  get estimatingCriteria(): JobOrderListEstimatingCriteria {
    return this.value.estimatingCriteria;
  }

  get orderSelection(): SelectionModel {
    return this.value.orderSelection;
  }

  get has6ADocument(): SelectionModel {
    return this.value.has6ADocument;
  }

  get selectionType(): JobOrderListSelectionType {
    return this.value.selectionType;
  }

  get options(): JobOrderListProduceOptions {
    return this.value.options;
  }

  get isProducing(): boolean {
    return this.value.produceStatus.producing;
  }

  get isJobSelected(): boolean {
    return isNotNil(getFormStateSnapshotValue(this.masterInfo).id);
  }

  get folderId(): ID {
    return getFormStateSnapshotValue(this.masterInfo).jobDocsFolderId;
  }

  get attachments(): JobOrderDetailsAttachment[] {
    return this.value.attachments;
  }

  get produceStatus(): JobOrderListProduceStatus {
    return this.value.produceStatus;
  }

  get produceStatus$(): Observable<JobOrderListProduceStatus> {
    return this.produceStatus$$.asObservable().pipe(filter$(Boolean));
  }

  get isJobCriteriaFailed(): boolean {
    return getFormStateSnapshotValue(this.masterInfo).isJobCriteriaFailed;
  }

  get ordersPrintCostCenter(): Maybe<number> {
    return this.estimatingCriteria.ordersPrintCostCenter;
  }

  get hasSelection(): boolean {
    return !isSelectionModelEmpty(this.value.orderSelection);
  }

  constructor(private userContext: UserContext) {
    super();
  }

  setOrderSelection(selection: SelectionModel): void {
    this.value.orderSelection = selection;
    this.emitChange();
    this.setDirty();
  }

  set6aDocument(is6A: SelectionModel): void {
    this.value.has6ADocument = is6A;
    this.emitChange();
    this.setDirty();
  }

  setSelectionType(type: JobOrderListSelectionType): void {
    if (this.value.selectionType !== type) {
      this.value.selectionType = type;
      this.resetSelection();
      this.emitChange();
    }
  }

  setOptions(options: JobOrderListProduceOptions): void {
    this.value.options = options;
    this.emitChange();
    this.setDirty();
  }

  setAttachments(attachments: JobOrderDetailsAttachment[]): void {
    this.value.attachments = attachments;
    this.emitChange();
    this.setDirty();
  }

  build(list: JobOrderList): void {
    this.setValue(list);
    this.updateMode();
    this.emitProduceStatus();

    this.costCenterCodes = sortBy(uniq(list.orders.map((order) => order.costCenterCode)));
    this.orderNumbers = sortBy(uniq(list.orders.map((order) => order.orderNumber)));

    this.emitChange();
    this.resetDirty();
  }

  reset(): void {
    this.value.orderSelection = emptySelectionModel();
    this.value.has6ADocument = emptySelectionModel();
    this.value.options = JOB_ORDER_LIST_DEFAULT_PRODUCE_OPTIONS;
    this.value.attachments = [];
    this.emitChange();
    this.resetDirty();
  }

  selectByFilter(filter: JobOrderListSelectionFilter): void {
    this.value.orderSelection = selectionModelSelectAll(
      emptySelectionModel(),
      this.value.orders.filter(createJobOrderListPredicate(filter)).map(getJobOrderListItemId));
    this.emitChange();
    this.updateDirtyBySelection();
  }

  switchToTrackingMode(status: JobOrderListProduceStatus): void {
    this.value.orderSelection = emptySelectionModel();
    this.value.has6ADocument = emptySelectionModel();
    this.setTrackingStatus(status);
  }

  setTrackingStatus(status: JobOrderListProduceStatus): void {
    this.value.produceStatus = status;
    this.updateMode();
    this.emitProduceStatus();
    this.emitChange();
  }

  private resetSelection(): void {
    this.value.orderSelection = this.value.selectionType === JobOrderListSelectionType.All
      ? newSelectionModel(false, this.value.orders.filter(isJobOrderSelectable).map(getJobOrderListItemId))
      : emptySelectionModel();
    this.emitChange();
    this.updateDirtyBySelection();
  }

  private updateDirtyBySelection(): void {
    this.setDirty(this.dirty || !isSelectionModelEmpty(this.value.orderSelection));
  }

  private updateMode(): void {
    const { isJobCriteriaFailed, ordersPrintCostCenter, isJobSelected, isProducing, masterInfo } = this;
    const hasProducePermission = this.userContext.hasPermission(BuilderPermission.JobAdmin.JobOrders.PRODUCE);
    this.setMode(hasProducePermission
      && isJobSelected
      && !isProducing
      && isJobEditable(getFormStateSnapshotValue(masterInfo))
      && (!isJobCriteriaFailed || isNotNil(ordersPrintCostCenter))
        ? FormMode.EditMode : FormMode.ViewMode);
  }

  private emitProduceStatus(): void {
    this.produceStatus$$.next(this.value.produceStatus);
  }
}
